<template>
  <div v-if="visible && consent">
    <div v-if="!choice" class="support-consent-card">
      <div class="support-consent-card__title">{{ view.title }}</div>
      <p class="support-consent-card__summary">{{ view.summary }}</p>
      <div class="support-consent-card__row">
        <span :class="['support-consent-card__dot', view.on ? 'on' : 'off']">{{ view.on ? "●" : "○" }}</span>
        <span class="support-consent-card__text">{{ view.text }}</span>
        <a-button v-if="view.action === 'ON'" type="primary" :disabled="busy" @click="openConsent">허용하기</a-button>
        <a-button v-else :disabled="busy" @click="turnOff">허용 끄기</a-button>
      </div>
      <p v-if="view.byText" class="support-consent-card__note">{{ view.byText }}</p>
      <p v-if="view.offlineText" class="support-consent-card__notice">{{ view.offlineText }}</p>
      <p class="support-consent-card__note">{{ SUPPORT_ACCOUNT_NOTE }}</p>
    </div>
    <!-- 동의 문구는 서버(GET /support/consent 의 text)를 그대로 그림 — 화면에 문구를 쓰지 않음(CI-21) -->
    <a-modal v-model:open="consentOpen" :title="view.title" :footer="null" :width="560" destroyOnClose>
      <div class="support-consent-text">
        <template v-for="(line, i) in bodyLines" :key="i">
          <div v-if="line.label" class="support-consent-item"><strong>{{ line.label }}</strong><span>{{ line.text }}</span></div>
          <p v-else class="support-consent-intro">{{ line.text }}</p>
        </template>
      </div>
      <p class="support-consent-version">문구 판: {{ consent.text && consent.text.version }}</p>
      <div class="support-consent-footer">
        <a-button @click="consentOpen = false">취소</a-button>
        <a-button type="primary" :loading="busy" @click="turnOn">동의하고 허용</a-button>
      </div>
    </a-modal>
    <!-- 첫 로그인 꼭 고르기 창(choice, 앱 껍데기에 하나): 닫기·ESC·바깥 클릭 없음, 고르면 서버가 needsChoice 를 끄고 창이 닫힘 -->
    <a-modal
      v-if="choice"
      :open="choiceOpen"
      :title="view.title"
      :footer="null"
      :width="560"
      :closable="false"
      :keyboard="false"
      :maskClosable="false"
    >
      <div class="support-consent-text">
        <template v-for="(line, i) in bodyLines" :key="i">
          <div v-if="line.label" class="support-consent-item"><strong>{{ line.label }}</strong><span>{{ line.text }}</span></div>
          <p v-else class="support-consent-intro">{{ line.text }}</p>
        </template>
      </div>
      <p v-if="choiceError" class="support-consent-card__notice" role="alert">{{ choiceError }}</p>
      <div class="support-consent-footer">
        <a-button :disabled="busy" @click="choose('OFF')">허용 안 함</a-button>
        <a-button type="primary" :loading="busy" @click="choose('ON')">허용</a-button>
      </div>
    </a-modal>
  </div>
</template>
<script>
// 「고객 지원을 위한 접근 허용」 동의 카드·첫 로그인 고르기 창(계약 SUPPORT 2·9, W6 C4). 회사 관리자만, 지원 탭에서는 늘 숨김.
// API: GET /support/consent · PUT /support/consent {status, textVersion}. 1년 재확인은 없음(10-31 결정 — 켠 동의는 누가 끌 때까지 그대로)
// 켤 때 2FA 세션이면 증표(M) — axios 의 기존 step-up 이 처리. 503 AUDIT_UNAVAILABLE = 바뀌지 않음.
import apiInstance from "@/axios";
import { oneBtnDialog, twoBtnDialog } from "@/utils/utils";
import { alreadyNotified } from "@/utils/mfa";
import { getAccessToken, isSupportTab } from "@/utils/tokenStore";
import subAccountService from "@/services/subAccount.service";
import { permState } from "@/services/permState";
import {
  consentView,
  consentBodyLines,
  consentCardVisible,
  consentChoiceNeeded,
  consentPutBody,
  consentErrorText,
  SUPPORT_ACCOUNT_NOTE,
  CONSENT_OFF_CONFIRM_TEXT,
  CONSENT_OFF_DONE_TEXT,
  CONSENT_ON_DONE_TEXT,
} from "@/utils/supportConsent";

export default {
  name: "SupportConsentCard",
  props: {
    // 앱 껍데기(App.vue)는 첫 로그인 고르기 창만
    choice: { type: Boolean, default: false },
    // 부모가 이미 /auth/profile 을 읽었으면 넘김(없으면 이 카드가 읽음)
    isCompanyManager: { type: Boolean, default: undefined },
  },
  data() {
    return { consent: null, manager: false, busy: false, consentOpen: false, choiceError: "", SUPPORT_ACCOUNT_NOTE };
  },
  computed: {
    ctx() {
      // 동의 보기 권한(서버가 늘 확인)이 없으면 부르지 않음 — 「계정 관리」 직원 403 알림(10-30)
      return { isCompanyManager: this.manager, supportTab: isSupportTab(), perms: permState.perms };
    },
    visible() {
      return consentCardVisible(this.ctx);
    },
    view() {
      return consentView(this.consent);
    },
    bodyLines() {
      return consentBodyLines(this.consent?.text?.body);
    },
    choiceOpen() {
      return consentChoiceNeeded(this.consent, this.ctx);
    },
  },
  watch: {
    isCompanyManager(value) {
      if (value === true && !this.manager) this.start(true);
    },
    // 권한(introspect)은 사이드 메뉴가 따로 읽어 늦게 올 수 있음 → 보이게 되면 그때 읽음
    visible(value) {
      if (value && !this.consent) this.load();
    },
  },
  async mounted() {
    // 고르기 창은 로그인한 일반 세션만(로그인 전 화면에서 /auth/profile 을 부르지 않음)
    if (isSupportTab() || (this.choice && !getAccessToken())) return;
    let manager = this.isCompanyManager;
    if (manager === undefined) {
      try {
        manager = (await subAccountService.getProfile())?.isCompanyManager === true;
      } catch (error) {
        manager = false;
      }
    }
    await this.start(manager === true);
  },
  methods: {
    // 읽기는 visible 감시 한 곳에서(관리자 표시·권한 중 늦게 온 쪽이 정함)
    start(manager) {
      this.manager = manager;
    },
    async load() {
      try {
        const { data } = await apiInstance.get("/support/consent", { showGlobalLoading: false });
        this.consent = data || null;
      } catch (error) {
        // 서버에 아직 없거나(옛 inapi) 권한이 없으면 카드를 그리지 않음
        this.consent = null;
      }
    },
    async save(request, doneText) {
      this.busy = true;
      try {
        const { data } = await request();
        this.consent = data || this.consent;
        this.consentOpen = false;
        if (doneText) await oneBtnDialog("완료", doneText, false);
      } catch (error) {
        if (!alreadyNotified(error)) await oneBtnDialog("바꾸지 못했습니다", consentErrorText(error), false);
        await this.load();
      } finally {
        this.busy = false;
      }
    },
    openConsent() {
      this.consentOpen = true;
    },
    async turnOn() {
      const body = consentPutBody("ON", this.consent);
      if (!body) return oneBtnDialog("허용할 수 없습니다", "동의 문구를 불러오지 못했습니다. 화면을 새로 고쳐 주세요.", false);
      await this.save(() => apiInstance.put("/support/consent", body), CONSENT_ON_DONE_TEXT);
    },
    async turnOff() {
      const ok = await twoBtnDialog("허용 끄기", CONSENT_OFF_CONFIRM_TEXT, "허용 끄기", "취소", { confirmStyle: "danger", focusCancel: true });
      if (!ok) return;
      await this.save(() => apiInstance.put("/support/consent", consentPutBody("OFF", this.consent)), CONSENT_OFF_DONE_TEXT);
    },
    // 고르기 창: 켜기는 turnOn 과 같은 요청(2FA 세션이면 axios step-up 이 증표 처리), 끄기는 확인 없이 바로. 실패는 창 안에
    async choose(status) {
      const body = consentPutBody(status, this.consent);
      if (!body) {
        this.choiceError = "동의 문구를 불러오지 못했습니다. 화면을 새로 고쳐 주세요.";
        return;
      }
      this.busy = true;
      this.choiceError = "";
      try {
        const { data } = await apiInstance.put("/support/consent", body);
        this.consent = data || this.consent;
      } catch (error) {
        // 인증번호 창을 닫았으면 실패 문구 없이 그대로(다시 고르면 됨)
        if (!error?.stepUpCancelled) this.choiceError = consentErrorText(error);
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
<style scoped>
.support-consent-card {
  margin: 0 0 16px;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}
.support-consent-card__title {
  font-weight: 700;
  margin-bottom: 8px;
}
.support-consent-card__row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.support-consent-card__summary {
  margin: 0 0 8px;
  color: #6b7280;
  font-size: 13px;
}
.support-consent-card__text {
  flex: 1;
  min-width: 240px;
}
.support-consent-card__dot.on {
  color: #16a34a;
}
.support-consent-card__dot.off {
  color: #9ca3af;
}
.support-consent-card__notice {
  margin: 8px 0 0;
  color: #b45309;
}
.support-consent-card__note {
  margin: 6px 0 0;
  color: #6b7280;
  font-size: 13px;
}
.support-consent-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 60vh;
  overflow-y: auto;
  line-height: 1.6;
  word-break: keep-all;
}
.support-consent-intro {
  margin: 0 0 4px;
  font-weight: 600;
}
.support-consent-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border-left: 3px solid #3292e3;
  border-radius: 4px;
  background: #eaf4fc;
}
.support-consent-item strong {
  color: #1f2937;
}
.support-consent-version {
  margin: 8px 0 0;
  color: #6b7280;
  font-size: 13px;
}
.support-consent-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
</style>
